import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { 
  Plus, 
  Trash2, 
  FileText,
  Send,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  Eye,
  Loader2,
  Bell
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const InvoicesTab = ({ businessId, token, authHeaders }) => {
  const { toast } = useToast();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_address: '',
    due_date: '',
    tax_rate: 0,
    notes: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }]
  });

  const fetchInvoices = async () => {
    if (!businessId) return;
    try {
      const [invoicesRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/mybookie/invoices/${businessId}`, authHeaders),
        axios.get(`${API}/api/mybookie/invoices/stats/${businessId}`, authHeaders)
      ]);
      setInvoices(invoicesRes.data.invoices);
      setStats(statsRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [businessId]);

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const tax = subtotal * (formData.tax_rate / 100);
    return { subtotal, tax, total: subtotal + tax };
  };

  const handleCreateInvoice = async () => {
    if (!formData.client_name || !formData.client_email || !formData.due_date) {
      toast({ title: "Error", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }

    if (formData.items.length === 0 || formData.items.some(i => !i.description || i.unit_price <= 0)) {
      toast({ title: "Error", description: "Please add at least one valid item", variant: "destructive" });
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/api/mybookie/invoices`, {
        business_id: businessId,
        ...formData
      }, authHeaders);
      
      toast({ title: "Success", description: "Invoice created" });
      setShowCreate(false);
      setFormData({
        client_name: '',
        client_email: '',
        client_address: '',
        due_date: '',
        tax_rate: 0,
        notes: '',
        items: [{ description: '', quantity: 1, unit_price: 0 }]
      });
      fetchInvoices();
    } catch (error) {
      toast({ title: "Error", description: "Failed to create invoice", variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await axios.post(`${API}/api/mybookie/invoices/${invoiceId}/send`, null, authHeaders);
      toast({ title: "Success", description: "Invoice marked as sent" });
      fetchInvoices();
    } catch (error) {
      toast({ title: "Error", description: "Failed to send invoice", variant: "destructive" });
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await axios.put(`${API}/api/mybookie/invoices/${invoiceId}`, {
        invoice_status: 'paid'
      }, authHeaders);
      toast({ title: "Success", description: "Invoice marked as paid" });
      fetchInvoices();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update invoice", variant: "destructive" });
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await axios.delete(`${API}/api/mybookie/invoices/${invoiceId}`, authHeaders);
      toast({ title: "Success", description: "Invoice deleted" });
      fetchInvoices();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete invoice", variant: "destructive" });
    }
  };

  const [sendingReminder, setSendingReminder] = useState(null);
  const handleSendReminder = async (invoiceId) => {
    setSendingReminder(invoiceId);
    try {
      const res = await axios.post(`${API}/api/mybookie/invoices/${invoiceId}/reminder`, null, authHeaders);
      toast({ 
        title: "Reminder Sent", 
        description: `Payment reminder sent${res.data.is_overdue ? ' (marked overdue)' : ''}. Reminder #${res.data.reminder_count}` 
      });
      fetchInvoices();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to send reminder", variant: "destructive" });
    } finally {
      setSendingReminder(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getStatusBadge = (status, dueDate) => {
    const today = new Date().toISOString().split('T')[0];
    if (status === 'sent' && dueDate < today) status = 'overdue';
    
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      overdue: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-500'
    };
    
    return (
      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${styles[status] || styles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#0A2B1D]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-[#E6E8E6]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-sm">
                  <Send className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#5C6661]">Outstanding</p>
                  <p className="text-lg font-semibold text-[#111614]">{formatCurrency(stats.total_outstanding)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-[#E6E8E6]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-50 rounded-sm">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#5C6661]">Paid</p>
                  <p className="text-lg font-semibold text-[#111614]">{formatCurrency(stats.total_paid)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-[#E6E8E6]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-sm">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-[#5C6661]">Overdue</p>
                  <p className="text-lg font-semibold text-[#111614]">{stats.overdue?.count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-[#E6E8E6]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#E2ECE7] rounded-sm">
                  <FileText className="w-5 h-5 text-[#0A2B1D]" />
                </div>
                <div>
                  <p className="text-xs text-[#5C6661]">Total Invoices</p>
                  <p className="text-lg font-semibold text-[#111614]">{stats.total_invoices}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header with Create Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-[#111614]">Invoices</h3>
        <Button 
          onClick={() => setShowCreate(true)}
          className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"
        >
          <Plus className="w-4 h-4 mr-2" /> Create Invoice
        </Button>
      </div>

      {/* Invoices List */}
      <Card className="border-[#E6E8E6]">
        <CardContent className="p-0">
          {invoices.length === 0 ? (
            <div className="p-8 text-center text-[#5C6661]">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No invoices yet. Create your first invoice!</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E6E8E6]">
              {invoices.map((invoice) => (
                <div key={invoice.id} className="p-4 hover:bg-[#F6F5F2] transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-medium text-[#111614]">{invoice.invoice_number}</span>
                        {getStatusBadge(invoice.status, invoice.due_date)}
                      </div>
                      <p className="text-sm text-[#5C6661]">{invoice.client_name}</p>
                      <p className="text-xs text-[#8A948F]">
                        Due: {invoice.due_date}
                        {invoice.reminder_count > 0 && (
                          <span className="ml-2 text-amber-600">&middot; {invoice.reminder_count} reminder{invoice.reminder_count > 1 ? 's' : ''} sent</span>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[#111614]">{formatCurrency(invoice.total)}</p>
                      <div className="flex gap-2 mt-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setShowDetail(invoice)}
                          className="text-[#5C6661] hover:text-[#111614]"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {invoice.status === 'draft' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleSendInvoice(invoice.id)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleSendReminder(invoice.id)}
                              disabled={sendingReminder === invoice.id}
                              className="text-amber-600 hover:text-amber-700"
                              data-testid={`reminder-btn-${invoice.id}`}
                              title="Send payment reminder"
                            >
                              {sendingReminder === invoice.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleMarkPaid(invoice.id)}
                              className="text-green-600 hover:text-green-700"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteInvoice(invoice.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Invoice Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#111614]">Create Invoice</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#111614]">Client Name *</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="Client or Company Name"
                  className="bg-[#FAFAF8] border-[#E6E8E6]"
                />
              </div>
              <div>
                <Label className="text-[#111614]">Client Email *</Label>
                <Input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  placeholder="client@example.com"
                  className="bg-[#FAFAF8] border-[#E6E8E6]"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#111614]">Client Address</Label>
              <Input
                value={formData.client_address}
                onChange={(e) => setFormData({ ...formData, client_address: e.target.value })}
                placeholder="Street, City, State, ZIP"
                className="bg-[#FAFAF8] border-[#E6E8E6]"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#111614]">Due Date *</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="bg-[#FAFAF8] border-[#E6E8E6]"
                />
              </div>
              <div>
                <Label className="text-[#111614]">Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                  className="bg-[#FAFAF8] border-[#E6E8E6]"
                />
              </div>
            </div>
            
            {/* Line Items */}
            <div>
              <Label className="text-[#111614] mb-2 block">Line Items</Label>
              <div className="space-y-2">
                {formData.items.map((item, index) => (
                  <div key={`item-${index}-${item.description}`} className="flex gap-2 items-center">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      className="flex-1 bg-[#FAFAF8] border-[#E6E8E6]"
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      className="w-20 bg-[#FAFAF8] border-[#E6E8E6]"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                      className="w-24 bg-[#FAFAF8] border-[#E6E8E6]"
                    />
                    <span className="w-24 text-right text-sm text-[#5C6661]">
                      {formatCurrency(item.quantity * item.unit_price)}
                    </span>
                    {formData.items.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleRemoveItem(index)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleAddItem}
                className="mt-2 border-[#E6E8E6]"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </div>
            
            {/* Totals */}
            <div className="bg-[#F6F5F2] p-4 rounded-sm">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#5C6661]">Subtotal</span>
                <span className="text-[#111614]">{formatCurrency(calculateTotal().subtotal)}</span>
              </div>
              {formData.tax_rate > 0 && (
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#5C6661]">Tax ({formData.tax_rate}%)</span>
                  <span className="text-[#111614]">{formatCurrency(calculateTotal().tax)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold border-t border-[#E6E8E6] pt-2 mt-2">
                <span className="text-[#111614]">Total</span>
                <span className="text-[#0A2B1D]">{formatCurrency(calculateTotal().total)}</span>
              </div>
            </div>
            
            <div>
              <Label className="text-[#111614]">Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Payment terms, thank you message, etc."
                className="bg-[#FAFAF8] border-[#E6E8E6]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-[#E6E8E6]">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateInvoice}
              disabled={creating}
              className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="sm:max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#111614]">
              Invoice {showDetail?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          
          {showDetail && (
            <div className="space-y-4 py-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-[#111614]">{showDetail.client_name}</p>
                  <p className="text-sm text-[#5C6661]">{showDetail.client_email}</p>
                  {showDetail.client_address && (
                    <p className="text-sm text-[#5C6661]">{showDetail.client_address}</p>
                  )}
                </div>
                {getStatusBadge(showDetail.status, showDetail.due_date)}
              </div>
              
              <div className="border-t border-[#E6E8E6] pt-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#5C6661]">
                      <th className="text-left py-2">Description</th>
                      <th className="text-right py-2">Qty</th>
                      <th className="text-right py-2">Price</th>
                      <th className="text-right py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {showDetail.items.map((item) => (
                      <tr key={`${item.description}-${item.quantity}-${item.unit_price}`} className="border-t border-[#E6E8E6]">
                        <td className="py-2 text-[#111614]">{item.description}</td>
                        <td className="py-2 text-right text-[#5C6661]">{item.quantity}</td>
                        <td className="py-2 text-right text-[#5C6661]">{formatCurrency(item.unit_price)}</td>
                        <td className="py-2 text-right text-[#111614]">{formatCurrency(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="bg-[#F6F5F2] p-4 rounded-sm">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#5C6661]">Subtotal</span>
                  <span className="text-[#111614]">{formatCurrency(showDetail.subtotal)}</span>
                </div>
                {showDetail.tax_amount > 0 && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#5C6661]">Tax ({showDetail.tax_rate}%)</span>
                    <span className="text-[#111614]">{formatCurrency(showDetail.tax_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold border-t border-[#E6E8E6] pt-2 mt-2">
                  <span className="text-[#111614]">Total</span>
                  <span className="text-[#0A2B1D]">{formatCurrency(showDetail.total)}</span>
                </div>
              </div>
              
              <div className="text-sm text-[#5C6661]">
                <p>Due: {showDetail.due_date}</p>
                {showDetail.sent_at && <p>Sent: {new Date(showDetail.sent_at).toLocaleDateString()}</p>}
                {showDetail.paid_at && <p>Paid: {new Date(showDetail.paid_at).toLocaleDateString()}</p>}
              </div>
              
              {showDetail.notes && (
                <div className="text-sm">
                  <p className="text-[#5C6661]">Notes:</p>
                  <p className="text-[#111614]">{showDetail.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesTab;
